import type { FC } from 'react';
import { InlineEditableText } from '@components/InlineEditableText';
import InputSelect from '@components/InputSelect';
import Tooltip from '@components/Tooltip';
import type { UIInputValue } from '@components/types';
import { responsiveTruncateClasses } from '@ContractBuilder/contract-builder-header/classes';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import type { EntityData } from '@domain/types/entity';
import { useBrandingOptions } from '@features/create-entity-modal/hooks/useBrandingOptions';
import { BriefcaseIcon } from '@heroicons/react/solid';
import { getDefaultPdfBranding } from '@root/helpers/pdf';
import { isString } from 'lodash-es';

import { useEditableField } from '../hooks';
import type { OnSaveFn } from '../hooks/use-editable-field';

interface EditableBrandingFieldProps {
  entity: EntityData;
  hasOperationsDisabled: boolean;
  onSave: OnSaveFn<string | undefined>;
}

export const EditableBrandingField: FC<EditableBrandingFieldProps> = ({ entity, hasOperationsDisabled, onSave }) => {
  const { brandingFieldName, data, isLoading } = useBrandingOptions();
  const hasUnsavedChanges = useDatapointsStore((state) => state.isDirty);
  const isDisabled = hasOperationsDisabled || hasUnsavedChanges;

  const selectedBranding = data.find((item) =>
    isString(item) ? item === entity?.[brandingFieldName] : item.value === entity?.[brandingFieldName],
  );

  const { onCancel, onChange, onClick, onUpdate, state } = useEditableField<string | undefined>({
    defaultValue: isString(selectedBranding) ? selectedBranding : selectedBranding?.value?.toString(),
    name: brandingFieldName,
    onSave,
  });

  if (isLoading) {
    return <div className="flex h-4 w-[120px] animate-pulse rounded-xl bg-slate-200" />;
  }

  return (
    <div className="flex items-center gap-1">
      <Tooltip content="Branding">
        <span>
          <BriefcaseIcon className="h-4 w-4" />
        </span>
      </Tooltip>
      <InlineEditableText
        editor={
          <InputSelect
            dataTestId="select:editable-branding"
            isDisabled={false}
            isFetchingOptions={isLoading}
            menuOuterClassName="min-w-28"
            name={brandingFieldName}
            onChange={(value) => onChange(value as string)}
            options={data}
            size="sm"
            value={state.value as UIInputValue}
          />
        }
        editMode={state.isEditing}
        disabled={isDisabled}
        display={
          isString(selectedBranding)
            ? (selectedBranding ?? getDefaultPdfBranding())
            : (selectedBranding?.name ?? 'No branding set')
        }
        onClick={onClick}
        onCancel={onCancel}
        onConfirm={onUpdate}
        confirmDisabled={hasUnsavedChanges}
        tooltipContent={!isDisabled ? 'Edit branding' : undefined}
        withEditIcon={false}
        className={responsiveTruncateClasses([
          'rounded-sm border border-transparent px-1 py-0.5',
          !isDisabled ? 'hover:border-gray-300' : '',
        ])}
      />
    </div>
  );
};
